import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import Modal from "../../../../../components/common/Modal";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import OutlinedButton from "../../../../../components/common/OutlinedButton";
import Spinner from "../../../../../components/common/Spinner";
import TextInput from "../../../../../components/forms/textInput";
import {
  ICashoutBank,
  ICreateCashoutBankBody,
  IUpdateCashoutBankBody,
} from "../../../../../setup/api/services/cashoutBanks/models";

/** Saudi IBANs: SA + 2 check digits + 20-character BBAN (24 total). The server's @Iban check stays authoritative. */
const IBAN_PATTERN = /^SA\d{2}[A-Z0-9]{20}$/;

const MAX_BANK_CODE = 10;
const MAX_SWIFT_CODE = 15;

interface ICashoutBankFormModalProps {
  open: boolean;
  /** Present ⇒ edit mode. */
  bank?: ICashoutBank | null;
  saving?: boolean;
  handleClose: () => void;
  handleConfirm: (
    // eslint-disable-next-line no-unused-vars
    body: ICreateCashoutBankBody | IUpdateCashoutBankBody
  ) => void;
}

interface IFormState {
  arabicName: string;
  englishName: string;
  accountName: string;
  bankCode: string;
  swiftCode: string;
  companyCode: string;
  accountNumber: string;
  iban: string;
}

const EMPTY_FORM: IFormState = {
  arabicName: "",
  englishName: "",
  accountName: "",
  bankCode: "",
  swiftCode: "",
  companyCode: "",
  accountNumber: "",
  iban: "",
};

/**
 * Create and edit in one form. Account numbers and IBANs are only ever returned masked, so in edit
 * mode those two fields start blank with the mask as a placeholder: an untouched credential field
 * is simply left out of the PUT rather than being re-sent, which is what the all-optional update
 * contract expects. Prefilling them with the mask would write "205***…883" as the real value.
 */
const CashoutBankFormModal = ({
  open,
  bank,
  saving = false,
  handleClose,
  handleConfirm,
}: ICashoutBankFormModalProps) => {
  const { t } = useTranslation();
  const isEdit = !!bank;

  const [form, setForm] = useState<IFormState>(EMPTY_FORM);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const markTouched = (field: keyof IFormState) => () =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  /**
   * A required field's border/message is driven from here, not from TextInput's own `required`
   * prop — that prop turns the border red the instant the field is focused, before the admin has
   * had a chance to type anything. Blur-based `touched` tracking gives the usual "only complain
   * once they've left it empty" behaviour instead.
   */
  const requiredError = (field: keyof IFormState) =>
    !isEdit && touched[field] && !form[field].trim()
      ? t("lbl_cashout_bank_field_required")
      : "";

  const requiredLabel = (label: string) =>
    isEdit ? label : `${label}${t("lbl_cashout_bank_required_suffix")}`;

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setTouched({});
      return;
    }
    setForm({
      arabicName: bank?.arabicName ?? "",
      englishName: bank?.englishName ?? "",
      accountName: bank?.accountName ?? "",
      bankCode: bank?.bankCode ?? "",
      swiftCode: bank?.swiftCode ?? "",
      // Plain text, not masked — safe to prefill, unlike accountNumber/iban below.
      companyCode: bank?.companyCode ?? "",
      // Masked server-side; never prefilled.
      accountNumber: "",
      iban: "",
    });
  }, [open, bank]);

  const setField = (field: keyof IFormState) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const normalizedIban = form.iban.trim().toUpperCase();

  const ibanFormatError = useMemo(() => {
    if (!normalizedIban) return "";
    return IBAN_PATTERN.test(normalizedIban)
      ? ""
      : t("lbl_cashout_bank_invalid_iban");
  }, [normalizedIban, t]);
  const ibanError = requiredError("iban") || ibanFormatError;

  /** Only the fields the admin actually typed or changed. */
  const changes = useMemo((): IUpdateCashoutBankBody => {
    const body: IUpdateCashoutBankBody = {};
    const optional: (keyof Pick<
      IFormState,
      | "arabicName"
      | "englishName"
      | "accountName"
      | "bankCode"
      | "swiftCode"
      | "companyCode"
    >)[] = [
      "arabicName",
      "englishName",
      "accountName",
      "bankCode",
      "swiftCode",
      "companyCode",
    ];

    optional.forEach((field) => {
      const next = form[field].trim();
      const current = (bank?.[field] ?? "") as string;
      if (!isEdit ? next !== "" : next !== current) {
        body[field] = next;
      }
    });

    const accountNumber = form.accountNumber.trim();
    if (accountNumber) body.accountNumber = accountNumber;
    if (normalizedIban) body.iban = normalizedIban;

    return body;
  }, [form, bank, isEdit, normalizedIban]);

  const canSubmit = useMemo(() => {
    if (saving || ibanError) return false;
    if (isEdit) return Object.keys(changes).length > 0;
    return (
      !!form.bankCode.trim() &&
      !!form.swiftCode.trim() &&
      !!form.companyCode.trim() &&
      !!form.accountNumber.trim() &&
      !!normalizedIban
    );
  }, [saving, ibanError, isEdit, changes, form, normalizedIban]);

  const onSubmit = () => {
    if (!canSubmit) return;
    if (isEdit) {
      handleConfirm(changes);
      return;
    }
    handleConfirm({
      ...changes,
      bankCode: form.bankCode.trim(),
      swiftCode: form.swiftCode.trim(),
      companyCode: form.companyCode.trim(),
      accountNumber: form.accountNumber.trim(),
      iban: normalizedIban,
    });
  };

  return (
    <Modal
      visible={open}
      onCancel={handleClose}
      customModalClassName={styles.modal}>
      <div className={styles.container}>
        <h1 className={styles.title}>
          {isEdit
            ? t("lbl_cashout_bank_edit_title")
            : t("lbl_cashout_bank_add_title")}
        </h1>
        <p className={styles.subtitle}>
          {isEdit
            ? t("lbl_cashout_bank_edit_subtitle")
            : t("lbl_cashout_bank_add_subtitle")}
        </p>

        <div className={styles.fields}>
          <TextInput
            label={t("lbl_cashout_bank_arabic_name")}
            value={form.arabicName}
            maxCharacters={100}
            onChange={(e) => setField("arabicName")(e.target.value)}
          />
          <TextInput
            label={t("lbl_cashout_bank_english_name")}
            value={form.englishName}
            maxCharacters={100}
            onChange={(e) => setField("englishName")(e.target.value)}
          />
          <TextInput
            label={t("lbl_cashout_bank_account_name")}
            hint={t("lbl_cashout_bank_account_name_hint")}
            value={form.accountName}
            maxCharacters={100}
            onChange={(e) => setField("accountName")(e.target.value)}
          />
          <div onBlur={markTouched("bankCode")}>
            <TextInput
              label={requiredLabel(t("lbl_cashout_bank_bank_code"))}
              value={form.bankCode}
              maxCharacters={MAX_BANK_CODE}
              dir="ltr"
              isError={!!requiredError("bankCode")}
              errorMessage={requiredError("bankCode")}
              onChange={(e) => setField("bankCode")(e.target.value)}
            />
          </div>
          <div onBlur={markTouched("swiftCode")}>
            <TextInput
              label={requiredLabel(t("lbl_cashout_bank_swift_code"))}
              value={form.swiftCode}
              maxCharacters={MAX_SWIFT_CODE}
              dir="ltr"
              isError={!!requiredError("swiftCode")}
              errorMessage={requiredError("swiftCode")}
              onChange={(e) => setField("swiftCode")(e.target.value)}
            />
          </div>
          <div onBlur={markTouched("companyCode")}>
            <TextInput
              label={requiredLabel(t("lbl_cashout_bank_company_code"))}
              value={form.companyCode}
              // The BE originally guessed 20 chars, which was wrong — OSV publishes no maximum,
              // so the only real cap left is the VARCHAR(255) column itself.
              maxCharacters={255}
              dir="ltr"
              isError={!!requiredError("companyCode")}
              errorMessage={requiredError("companyCode")}
              onChange={(e) => setField("companyCode")(e.target.value)}
            />
          </div>
          <div onBlur={markTouched("accountNumber")}>
            <TextInput
              label={requiredLabel(t("lbl_cashout_bank_account_number"))}
              value={form.accountNumber}
              maxCharacters={34}
              dir="ltr"
              placeholder={isEdit ? bank?.accountNumber : undefined}
              hint={
                isEdit ? t("lbl_cashout_bank_keep_current_hint") : undefined
              }
              isError={!!requiredError("accountNumber")}
              errorMessage={requiredError("accountNumber")}
              onChange={(e) => setField("accountNumber")(e.target.value)}
            />
          </div>
          <div onBlur={markTouched("iban")}>
            <TextInput
              label={requiredLabel(t("lbl_cashout_bank_iban"))}
              value={form.iban}
              maxCharacters={34}
              dir="ltr"
              placeholder={isEdit ? bank?.iban : undefined}
              hint={
                isEdit ? t("lbl_cashout_bank_keep_current_hint") : undefined
              }
              isError={!!ibanError}
              errorMessage={ibanError}
              onChange={(e) => setField("iban")(e.target.value)}
            />
          </div>
        </div>

        {!isEdit && (
          <p className={styles.note}>
            {t("lbl_cashout_bank_created_disabled_note")}
          </p>
        )}

        <div className={styles.actions}>
          <PrimaryButton disabled={!canSubmit} onClick={onSubmit}>
            {saving ? (
              <Spinner />
            ) : isEdit ? (
              t("lbl_cashout_bank_save")
            ) : (
              t("lbl_cashout_bank_add")
            )}
          </PrimaryButton>
          <OutlinedButton disabled={saving} onClick={handleClose}>
            {t("lbl_cancel")}
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default CashoutBankFormModal;
